import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './PhotoGalleryModal.css';

const PhotoGalleryModal = ({
  isOpen,
  onClose,
  images = [],
  projectName,
  language = 'ar'
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Block underlying map from receiving touches on iOS
      document.body.classList.add('photo-gallery-modal-open');
      // Reset states when opening
      setCurrentImageIndex(0);
      setIsLoading(true);
      setHasError(false);
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('photo-gallery-modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('photo-gallery-modal-open');
    };
  }, [isOpen]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
    setIsLoading(true);
    setHasError(false);
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
    setIsLoading(true);
    setHasError(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        language === 'ar' ? handleNext() : handlePrevious();
        break;
      case 'ArrowRight':
        language === 'ar' ? handlePrevious() : handleNext();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, language]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentImageIndex];

  const stopTouchPropagation = (e) => {
    e.stopPropagation();
  };

  // Render outside ParcelDetailsModal's pointer-events:none overlay
  // so iOS reliably routes touches to the gallery instead of the map.
  return createPortal(
    <div
      className={`photo-gallery-modal ${isOpen ? 'open' : ''}`}
      onClick={onClose}
      onTouchStart={stopTouchPropagation}
      onTouchMove={stopTouchPropagation}
      onTouchEnd={stopTouchPropagation}
    >
      <div
        className="photo-gallery-overlay"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={stopTouchPropagation}
        onTouchMove={stopTouchPropagation}
        onTouchEnd={stopTouchPropagation}
      >
        {/* Header */}
        <div className="photo-gallery-header">
          <div className="photo-gallery-title">
            <h3>{projectName}</h3>
            <span className="image-counter">
              {currentImageIndex + 1} / {images.length}
            </span>
          </div>
          <button
            className="photo-gallery-close"
            onClick={onClose}
            aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
          >
            ×
          </button>
        </div>

        {/* Main Content */}
        <div className="photo-gallery-content">
          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button 
                className={`photo-gallery-nav prev ${language === 'ar' ? 'rtl' : 'ltr'}`}
                onClick={language === 'ar' ? handleNext : handlePrevious}
                aria-label={language === 'ar' ? 'الصورة التالية' : 'Previous image'}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path 
                    d={language === 'ar' ? "M9 18l6-6-6-6" : "M15 18l-6-6 6-6"} 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              
              <button 
                className={`photo-gallery-nav next ${language === 'ar' ? 'rtl' : 'ltr'}`}
                onClick={language === 'ar' ? handlePrevious : handleNext}
                aria-label={language === 'ar' ? 'الصورة السابقة' : 'Next image'}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path 
                    d={language === 'ar' ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Image Container */}
          <div className="photo-gallery-image-container">
            {isLoading && (
              <div className="photo-gallery-loading">
                <div className="loading-spinner"></div>
                <p>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
              </div>
            )}

            {hasError ? (
              <div className="photo-gallery-error">
                <p>{language === 'ar' ? 'خطأ في تحميل الصورة' : 'Error loading image'}</p>
              </div>
            ) : (
              <img
                src={currentImage}
                alt={`${projectName} - ${language === 'ar' ? 'صورة' : 'Image'} ${currentImageIndex + 1}`}
                className="photo-gallery-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  display: isLoading ? 'none' : 'block'
                }}
              />
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="photo-gallery-thumbnails">
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`photo-gallery-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setIsLoading(true);
                    setHasError(false);
                  }}
                >
                  <img
                    src={image}
                    alt={`${language === 'ar' ? 'صورة مصغرة' : 'Thumbnail'} ${index + 1}`}
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoGalleryModal;
